"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { CategoryTreeNode } from "@/lib/api-types";

interface Props {
  categories: CategoryTreeNode[];
}

/**
 * Mobile-only бургер + поиск. На >= md компонент скрыт.
 * Поиск раскрывается в полную ширину поверх header'а; меню — нативный <dialog>.
 */
export function MobileMenu({ categories }: Props) {
  const router = useRouter();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const drawerRef = useRef<HTMLDialogElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const openDrawer = () => drawerRef.current?.showModal();
  const closeDrawer = () => drawerRef.current?.close();

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim()) {
      router.push(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <div className="flex items-center gap-1 md:hidden">
      <button
        type="button"
        aria-label="Поиск"
        onClick={() => setSearchOpen((v) => !v)}
        className="flex h-11 w-11 items-center justify-center rounded-[8px] hover:bg-bg-muted"
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-5 w-5"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Меню"
        onClick={openDrawer}
        className="flex h-11 w-11 items-center justify-center rounded-[8px] hover:bg-bg-muted"
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-5 w-5"
        >
          <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
        </svg>
      </button>

      {/* Поисковая строка поверх header'а */}
      {searchOpen && (
        <div className="fixed inset-x-0 top-16 z-30 border-b border-border bg-white px-4 py-3 shadow-sm md:hidden">
          <form role="search" onSubmit={onSearchSubmit} className="flex items-center gap-2">
            <input
              ref={searchInputRef}
              type="search"
              name="q"
              placeholder="Поиск: артикул или название…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск по каталогу"
              className="h-11 flex-1 rounded-[8px] border border-border bg-white px-3 text-base focus-visible:outline-2 focus-visible:outline-brand-500"
            />
            <button
              type="button"
              onClick={() => {
                setSearchOpen(false);
                setQ("");
              }}
              className="h-11 px-3 text-sm text-text-muted"
            >
              Отмена
            </button>
          </form>
        </div>
      )}

      {/* Drawer с меню — нативный <dialog> для focus-trap + Escape */}
      <dialog
        ref={drawerRef}
        aria-label="Главное меню"
        className="m-0 h-dvh max-h-none w-[90vw] max-w-sm bg-white p-0 backdrop:bg-black/40 md:hidden"
        style={{ marginLeft: "auto", marginRight: 0 }}
        onClick={(e) => {
          // Закрыть при клике вне диалога (на backdrop).
          if (e.target === drawerRef.current) closeDrawer();
        }}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-4">
          <span className="text-base font-semibold">Меню</span>
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Закрыть"
            className="flex h-9 w-9 items-center justify-center rounded-[8px] hover:bg-bg-muted"
          >
            <svg
              aria-hidden
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-5 w-5"
            >
              <path d="m6 6 12 12M6 18 18 6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col gap-1 p-3" aria-label="Категории и страницы">
          <Link
            href="/catalog"
            onClick={closeDrawer}
            className="flex h-11 items-center rounded-[8px] px-3 text-base font-semibold hover:bg-bg-muted"
          >
            Весь каталог
          </Link>
          <div className="my-2 border-t border-border" />
          <div className="max-h-[50vh] overflow-y-auto">
            {[...categories]
              .sort((a, b) => {
                const aOrder = a.sort_order || Number.MAX_SAFE_INTEGER;
                const bOrder = b.sort_order || Number.MAX_SAFE_INTEGER;
                return aOrder - bOrder;
              })
              .map((c) => (
                <Link
                  key={c.id}
                  href={`/catalog/${c.slug}`}
                  onClick={closeDrawer}
                  className="flex h-11 items-center rounded-[8px] px-3 text-sm hover:bg-bg-muted"
                >
                  {c.name}
                </Link>
              ))}
          </div>
          <div className="my-2 border-t border-border" />
          <Link
            href="/delivery"
            onClick={closeDrawer}
            className="flex h-11 items-center rounded-[8px] px-3 text-sm hover:bg-bg-muted"
          >
            Доставка
          </Link>
          <Link
            href="/payment"
            onClick={closeDrawer}
            className="flex h-11 items-center rounded-[8px] px-3 text-sm hover:bg-bg-muted"
          >
            Оплата
          </Link>
          <Link
            href="/contacts"
            onClick={closeDrawer}
            className="flex h-11 items-center rounded-[8px] px-3 text-sm hover:bg-bg-muted"
          >
            Контакты
          </Link>
          <div className="my-2 border-t border-border" />
          <a
            href="mailto:sales@xn--j1ahie.xn--80adxhks"
            onClick={closeDrawer}
            className="flex h-11 items-center rounded-[8px] px-3 text-sm text-brand-700 hover:bg-bg-muted"
          >
            sales@сток.москва
          </a>
        </nav>
      </dialog>
    </div>
  );
}
